import React, { useState, useRef, useEffect } from 'react';
import { Filter, Check } from 'lucide-react';

/**
 * Shared "Filter" pill + sort popover for the firm's list surfaces (Portfolios,
 * Manual). The caller owns the sort state and decides which dimensions apply to
 * the list it is rendering - a tab that does not render a column must not offer
 * it here.
 *
 *   value    { field, dir }  dir is 'asc' | 'desc'
 *   onChange (next)          called with the same shape
 *   fields   [{ key, label }]
 *   darkMode                 the surfaces this lives on are inline-styled off
 *                            useTheme().darkMode, so the theme is a prop, not a
 *                            hook read - the control stays usable anywhere.
 */
export const SORT_FIELDS = {
  MARKET_VALUE: 'market_value',
  ACCOUNT_COUNT: 'account_count',
  INCEPTION: 'inception',
  ACTIVITY: 'activity',
  ALPHA: 'alpha',
};

const DIRECTIONS = [
  { key: 'desc', label: 'Descending' },
  { key: 'asc', label: 'Ascending' },
];

export default function SortFilterControl({ value, onChange, fields, darkMode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const textPrimary = darkMode ? '#FFFFFF' : '#0A0A0A';
  const textMuted = darkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.50)';
  const hairlineStrong = darkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.22)';
  const rowHover = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const panelBg = darkMode ? '#141414' : '#FFFFFF';

  const sectionLabel = {
    padding: '8px 12px 4px',
    fontSize: 10,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.16em',
    color: textMuted,
  };

  // One row per choice. Selected rows carry a check so field and direction are
  // both legible at a glance; the panel stays open so picking a field and then
  // a direction is a single visit.
  const option = (label, selected, onPick) => (
    <button
      key={label}
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onPick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '7px 12px',
        border: 'none',
        background: 'transparent',
        color: selected ? textPrimary : textMuted,
        fontSize: 13,
        fontFamily: 'Inter, sans-serif',
        textAlign: 'left',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = rowHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{ width: 12, display: 'inline-flex' }}>
        {selected && <Check size={12} />}
      </span>
      {label}
    </button>
  );

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 16px',
          borderRadius: 9999,
          border: `1px solid ${open ? textPrimary : hairlineStrong}`,
          background: 'transparent',
          color: open ? textPrimary : textMuted,
          fontSize: 14,
          fontFamily: 'Inter, sans-serif',
          cursor: 'pointer',
          transition: 'border-color 150ms, color 150ms',
        }}
      >
        <Filter size={13} />
        Filter
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 6,
            zIndex: 20,
            minWidth: 200,
            padding: '4px 0 6px',
            borderRadius: 6,
            border: `1px solid ${hairlineStrong}`,
            background: panelBg,
            boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
          }}
        >
          <div style={sectionLabel}>Sort by</div>
          {fields.map((f) =>
            option(f.label, value.field === f.key, () =>
              onChange({ field: f.key, dir: value.dir })
            )
          )}
          <div style={{ ...sectionLabel, borderTop: `1px solid ${hairlineStrong}`, marginTop: 4 }}>
            Order
          </div>
          {DIRECTIONS.map((d) =>
            option(d.label, value.dir === d.key, () =>
              onChange({ field: value.field, dir: d.key })
            )
          )}
        </div>
      )}
    </div>
  );
}
